import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HtmlContent from "../html-content/HtmlContent";
import "./CustomContentModal.scss";
import CustomButton from "../../components/custom-button/CustomButton";
import Grid from "@mui/material/Grid2";
import { BTN_CANCEL, BTN_COMPLETE } from "../../utility/constants/locale";
import { useSaveContentStatusMutation } from "./SaveContentStatusAPISlice";
import { CONTENT_STATUS_COMPLETED_ID } from "../../utility/constants/common-constants";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { enqueueSnackbar } from "notistack";
import { invalidateEducationLibrary } from "../../features/education-library/services/GetEducationLibraryAPISlice";
import DOMPurify from "dompurify";
import CustomSpinner from "../custom-spinner/CustomSpinner";
import { useTranslation } from "react-i18next";

interface CustomContentModalProps {
  open: boolean;
  onClose: () => void;
  htmlContent?: string;
  subtitle?: string;
  confirmationBox?: boolean;
  modalTitle?: string;
  casePlanMilestoneMappingId?: string;
  activityCompletionStatus?: string;
  patientProfileId?: number;
  lang?: string;
}

const CustomContentModal: React.FC<CustomContentModalProps> = ({
  open,
  onClose,
  htmlContent,
  subtitle,
  confirmationBox,
  modalTitle,
  casePlanMilestoneMappingId,
  activityCompletionStatus,
  patientProfileId,
  lang,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const onCloseModal = () => {
    onClose();
    setIsChecked(false);
  };
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [isChecked, setIsChecked] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const sanitizedContent = DOMPurify.sanitize(htmlContent || '');
  const patientUserName = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.userName
  );
  const [contentStatus, { isLoading, isSuccess, isError, error }] =
    useSaveContentStatusMutation();

  const saveContentTaskStatus = async (values: any) => {
    try {
      const body = {
        casePlanMilestoneMappingId: casePlanMilestoneMappingId,
        statusId: CONTENT_STATUS_COMPLETED_ID,
        updatedBy: patientUserName || "Patient",
      };

      const response = await contentStatus({ body }).unwrap();
      if (patientProfileId) {
        invalidateEducationLibrary(dispatch, patientProfileId, lang!);
      }
      onCloseModal(); // Closing the Modal
      enqueueSnackbar(response.updateStatus.value, { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar("Error in saving the update", { variant: "error" });
    }
  };

  const pendingStatuses = [
    t("inProgress"),
    t("pending"),
    t("taskPending"),
    t("notCompleted"),
    t("initiated"),
    t("notStarted"),
  ];

  const currentStatus = pendingStatuses.includes(
    activityCompletionStatus ? activityCompletionStatus : ""
  );

  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          setIsScrolledToEnd(true);
        }
      }
    };

    const attachScrollListener = () => {
      const contentElement = contentRef.current;
      if (contentElement) {
        contentElement.addEventListener("scroll", handleScroll);
      }
    };

    const detachScrollListener = () => {
      const contentElement = contentRef.current;
      if (contentElement) {
        contentElement.removeEventListener("scroll", handleScroll);
      }
    };

    // Reset and reattach listener when modal opens
    if (open) {
      setIsScrolledToEnd(false);
      setTimeout(attachScrollListener, 100); // Ensure DOM is rendered
    } else {
      detachScrollListener();
    }

    // Cleanup listener on unmount or modal close
    return () => detachScrollListener();
  }, [open]);

  if (isLoading) {
    return <CustomSpinner />;
  }

  return (
    <Modal
      open={open}
      onClose={onCloseModal}
      aria-labelledby="content-modal-title"
      aria-describedby="content-modal-description"
    >
      <Box className="modalBox">
        <Box position="relative">
          <IconButton
            onClick={onCloseModal}
            aria-label="Close content modal"
            sx={{
              position: "absolute",
              top: 0,
              right: -11,
              color: "grey.500",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box className="modalTitle" aria-labelledby="modal-title">
            {modalTitle}
          </Box>
          <Box className="modalWarapper">
            <Box
              ref={contentRef}
              className="modalContent"
              aria-labelledby="modal-content"
              sx={{
                margin: "1rem 0",
                // Custom scrollbar styles
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {confirmationBox && currentStatus && (
              <Grid
                container
                spacing={2}
                sx={{
                  flexDirection: {
                    xs: "column", // Stack vertically on mobile
                    md: "row", // Align horizontally on desktop
                  },
                  paddingTop: "16px",
                }}
              >
                {/* Checkbox Grid */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={() => setIsChecked(!isChecked)}
                        disabled={!isScrolledToEnd}
                      />
                    }
                    label={t("confirmationalTextEducationalContent")}
                  />
                </Grid>

                {/* Buttons Grid */}
                <Grid
                  container
                  size={{ xs: 12, md: 4 }}
                  spacing={2}
                  justifyContent={{
                    xs: "center", // Center buttons on mobile
                    md: "flex-end", // Align buttons to the right on desktop
                  }}
                >
                  <Grid
                    size={{ xs: 6 }}
                    sx={{ display: "flex", justifyContent: "end" }}
                  >
                    <CustomButton
                      variant="outlined"
                      onClick={onCloseModal}
                      text={BTN_CANCEL}
                      sx={{
                        padding: "8px 2rem",
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <CustomButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      onClick={saveContentTaskStatus}
                      text={BTN_COMPLETE}
                      disabled={!isChecked}
                      sx={{
                        padding: "8px 2rem",
                        "&.Mui-disabled": {
                          backgroundColor: "#A41E35",
                          color: "#fff",
                          opacity: "40%",
                          boxShadow:
                            "0px 2px 2px 0px rgba(0, 0, 0, 0.2509803922)",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CustomContentModal;
